import {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { InfiniteScrollDropdown } from '../../../common/InfiniteScroll/InfiniteScrollDropdown';
import UpAngleIcon from '../../../common/assets/svgIcons/ArrowUpIcon';
import DownAngleIcon from '../../../common/assets/svgIcons/DownAngleIcon';
import Cross from '../../assets/Cross';
import {
  greyColor, maxTablet, primaryColor, primaryFont, textBlack,
} from '../../../../Utils/utilsStyle';

const DropdownRoot = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

const Trigger = styled.button<{ $active: boolean; $open: boolean }>`
  width: 100%;
  height: 36px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-radius: 6px;
  border: 1px solid ${({ $open }) => ($open ? primaryColor : '#DADADA')};
  background: #ffffff;
  text-align: left;
  font-family: ${primaryFont};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${primaryColor};
  }
`;

const TriggerText = styled.span<{ $active: boolean; $clamp?: boolean }>`
  display: inline-flex;
  color: ${({ $active }) => ($active ? textBlack : '#00000040')};
  font-family: ${primaryFont};
  font-size: 14px;
  letter-spacing: -0.4px;
  user-select: none;
  @media all and (max-width: ${maxTablet}) {
    font-size: 12px;
  }
  ${({ $clamp }) => $clamp && `
    display: block;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

const TriggerIcons = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  > svg {
    width: 10px;
    height: 7px;
  }
`;

const Separator = styled.div`
  width: 1px;
  height: 18px;
  background-color: #DDD;
  margin: 0 7px 0 10px;
`;

const ClearIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover svg path {
    fill: ${greyColor};
  }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  padding: 8px 0;
`;

interface CustomDropdownProps {
  options: { id: number; label: string; image?: string; subtitle?: string }[];
  selectedId: number;
  onSelect: (id: number) => void;
  placeholder: string;
  hasSearch?: boolean;
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight' | 'top' | 'bottom' | 'left' | 'right';
  hasMore?: boolean;
  loadMore?: () => void;
  isLoading?: boolean;
  maxHeight?: string;
  searchPlaceholder?: string;
  onSearchSubmit?: (term: string) => void;
  useRemoteSearch?: boolean;
  variant?: 'text' | 'iconText' | 'iconTextDropdown' | 'customIconTextDropdown';
  isCardOption?: boolean;
  onDropdownOpen?: () => void;
  initialSearchTerm?: string;
  clearable?: boolean;
  clampLabel?: boolean;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = memo(({
  options,
  selectedId,
  onSelect,
  placeholder,
  hasSearch = false,
  hasMore = false,
  loadMore = () => { },
  isLoading = false,
  maxHeight = '300px',
  searchPlaceholder = 'Search...',
  onSearchSubmit,
  useRemoteSearch,
  isCardOption,
  onDropdownOpen,
  initialSearchTerm,
  clearable = false,
  clampLabel = false,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pendingId, setPendingId] = useState(selectedId);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.id === selectedId),
    [options, selectedId],
  );
  const selectedLabel = selectedOption?.label || placeholder;
  const isActive = selectedId !== 0 && selectedId !== undefined && !!selectedOption;

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    onDropdownOpen?.();
    setPendingId(selectedId);
    setOpen(true);
  };

  const handleSelect = useCallback((id: number) => {
    setPendingId(id);
    onSelect(id);
    setOpen(false);
  }, [onSelect]);

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onSelect(0);
  };

  return (
    <DropdownRoot ref={rootRef} className="advance-search-dropdown">
      <Trigger
        type="button"
        $active={open || isActive}
        $open={open}
        onClick={handleTriggerClick}
      >
        <TriggerText $active={open || isActive} $clamp={clampLabel}>
          {selectedLabel}
        </TriggerText>
        <TriggerIcons>
          {clearable && isActive && (
            <ClearIconWrapper className="dropdown-clear-icon" onClick={handleClear}>
              <Cross />
            </ClearIconWrapper>
          )}
          {clearable && isActive && <Separator />}
          {open ? <UpAngleIcon /> : <DownAngleIcon color={greyColor} />}
        </TriggerIcons>
      </Trigger>
      {open && (
        <Panel onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
          <InfiniteScrollDropdown
            options={options}
            selectedId={pendingId}
            onSelect={handleSelect}
            hasSearch={hasSearch}
            hasMore={hasMore}
            loadMore={loadMore}
            isLoading={isLoading}
            maxHeight={maxHeight}
            searchPlaceholder={searchPlaceholder}
            onSearchSubmit={onSearchSubmit}
            useRemoteSearch={useRemoteSearch}
            isCardOption={isCardOption}
            initialSearchTerm={initialSearchTerm}
          />
        </Panel>
      )}
    </DropdownRoot>
  );
});

CustomDropdown.displayName = 'CustomDropdown';
